import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isExtraInfoOrMeta } from 'emergency_data/src/components/JurisdictionView/helpers';
import {
  trackHoverOnCallWithTime,
  trackHoverOnPin,
  trackHoverOnCall,
  trackClickOnCallFromMap,
  trackCallMarkerClicks,
} from 'emergency_data/src/components/JurisdictionView/jurisdictionViewTrackings';
import { ANI_ALI } from 'emergency_data/src/components/Location/constants/dataSources';
import {
  getDataSource,
  isSourceAniAli,
} from 'emergency_data/src/components/Location/helpers';
import {
  getMostRecentLoc,
  getMostRecentLocations,
} from 'emergency_data/src/utils/getMostRecentLoc';
import isEqual from 'lodash.isequal';
import { INTERNATIONAL_SUPPORT } from '@rsos/capstone/src/constants/capabilities';
import {
  showIncidentPopover,
  hideIncidentPopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import {
  BE_CALL_ALERTING,
  BE_CALL_ANSWERED,
  BE_HOLD_CALL,
  BE_END_CALL,
  BE_RETRIEVE_CALL,
} from '@rsos/constants/callStatuses';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { SOURCE_SMS } from '@rsos/constants/locationSources';
import { LANDLINE, VOIP, OTHER, PHASE } from '@rsos/constants/locationTypes';
import { ESRI, GOOGLE } from '@rsos/maps/constants/mapProviders';
import {
  setSelectedCallerID,
  resetNumberWithNewLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setHoveredCallerID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import { isCallerIDFromOrg } from '@rsos/utils/isCallerIDFromOrg';
import {
  panGoogleMapTo,
  setGoogleMapZoomTo,
} from '@rsos/utils/panMapToCoordinate';
import useIsIRP from '@rsos/utils/useIsIRP';
import usePortalType from '@rsos/utils/usePortalType';
import usePrevious from '@rsos/utils/usePrevious';
import ICON_MAPPING from '../../assets/calls/sprites-output/sprites-info.json';
import callsSprites from '../../assets/calls/sprites-output/sprites_2x.png';
import CallsLayer, {
  CALL_ICONS_LAYER,
  SECONDARY_LOCATION_ICON_LAYER,
} from '../RSOSLayers/CallsLayer';
import storeLayers from '../storeLayers';

// TODO v2: there are many imports to emergency_data. consider moving them out or adding new

const getPrimaryCallIconName = ({
  location,
  isAllView,
  isHistoricLocation,
  hasCallStatusAccess,
  callQueue,
  hasAniAliAccess,
}) => {
  const callStatus = getMostRecentCallStatus({
    callerID: location?.caller_id,
    callQueue,
    hasAniAliAccess,
    hasCallStatusAccess,
  });

  const isCallRinging = callStatus === BE_CALL_ALERTING;
  const isCallAnswered =
    callStatus === BE_CALL_ANSWERED || callStatus === BE_RETRIEVE_CALL;
  const isCallOnHold = callStatus === BE_HOLD_CALL;
  const isCallReleased = callStatus === BE_END_CALL;
  if (isAllView) {
    if (location?.source === SOURCE_SMS || location?.isTT911) {
      return 'allcall_textto911';
    } else if (hasCallStatusAccess && callStatus) {
      if (isCallRinging) {
        return 'call_status_ringing';
      } else if (isCallAnswered) {
        return 'call_status_answered';
      } else if (isCallOnHold) {
        return 'call_status_on_hold';
      } else if (isCallReleased) {
        return 'call_status_released';
      } else {
        return 'allcall_marker';
      }
    } else {
      return 'allcall_marker';
    }
  } else {
    if (isHistoricLocation) {
      return 'historic_call_marker';
    } else if (location?.source === SOURCE_SMS || location?.isTT911) {
      return 'sms_selected';
    } else {
      return 'call_selected';
    }
  }
};

const getSecondaryCallIconName = ({ isAllView, location }) => {
  const isLandlineOrOthers =
    (location && location?.class_of_service_category === LANDLINE) ||
    (location && location?.class_of_service_category === OTHER);

  const isVOIP = location?.class_of_service_category === VOIP;
  // TODO: add these icons to the sprite

  if (isAllView) {
    return 'allcall_marker';
  } else {
    if (isLandlineOrOthers) {
      return 'call_landline';
    } else if (isVOIP) {
      return 'call_voip';
    } else {
      return 'call_ali';
    }
  }
};

const MARKER_SIZES = {
  sizeScale: 60, // this is the factor to scale the icon.
  sizeMinPixels: 38, // when zoomed out, the max height of the icon.
  sizeMaxPixels: 40, // when zoomed in, the max height of the icon.
};

const CallsGL = ({ map, overlay, activeQueueTab }) => {
  const [hoveredCallData, setHoveredCallData] = useState(null);
  const [normalizedData, setNormalizedData] = useState({
    primaryLocations: [],
    secondaryLocations: [],
  });
  const [timer, setTimer] = useState(0);
  const hoveredTimer = useRef(null);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const prevSelectedCallerID = usePrevious(selectedCallerID);
  const numberWithNewLocation = useSelector(
    state => state.emergencyCalls.numberWithNewLocation,
  );

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  const isAllView = useMemo(() => !selectedAlertID && !selectedCallerID, [
    selectedAlertID,
    selectedCallerID,
  ]);
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const { isPremiumUser, portalType } = usePortalType();

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const hasCallStatusAccess =
    useSelector(state =>
      checkCallStatusAccessSelector({
        psaps: state.psaps,
      }),
    ) && hasAniAliAccess;

  const callQueue = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  const isInternational = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP.activeCapabilities &&
        state.psaps.currentPSAP.activeCapabilities[INTERNATIONAL_SUPPORT]
      ),
  );

  const hoveredCallerID = useSelector(
    state => state.hoveredIncident.hoveredCallerID,
  );

  const { irpVersion } = useIsIRP();
  const dispatch = useDispatch();

  // handles hovering over Secondary Location
  const handleHoverSecondaryLocation = useCallback(
    ({ location }) => {
      if (map) {
        const isSecondaryLocation = location?.class_of_service_category?.includes(
          PHASE,
        );
        if (location && isSecondaryLocation) {
          // SECONDARY LOCATION
          setHoveredCallData(location);
          dispatch(showIncidentPopover(location));
          handleSecondaryLocationTracking(location);
          overlay.setCursorPointer();
        } else {
          setHoveredCallData(null);
          dispatch(hideIncidentPopover());
          overlay.setCursorDefault();
        }
      }
    },
    [dispatch, handleSecondaryLocationTracking, map, overlay],
  );

  // handles hovering over Primary Location
  const handleHoverPrimaryLocation = useCallback(
    ({ location }) => {
      if (map) {
        if (location) {
          setHoveredCallData(location);
          dispatch(showIncidentPopover(location));
          handlePrimaryLocationTracking(location, false, 0);
        } else {
          setHoveredCallData(null);
          dispatch(hideIncidentPopover());
          // Reset timer for timer based tracking
          setTimer(0);
          overlay.setCursorDefault();
        }
      }
    },
    [dispatch, handlePrimaryLocationTracking, map, overlay],
  );

  const handleSecondaryLocationTracking = useCallback(
    location => {
      const { payloadID, requestID } = isExtraInfoOrMeta(location);
      const isConnectedCar = isCallerIDFromOrg(
        'sxm',
        callQueue,
        location?.caller_id,
      );

      const callStatus = getMostRecentCallStatus({
        callerID: location?.caller_id,
        callQueue,
        hasAniAliAccess,
        hasCallStatusAccess,
      });
      trackHoverOnCall(
        isConnectedCar,
        irpVersion,
        payloadID,
        requestID,
        portalType,
        ANI_ALI,
        location.class_of_service,
        callStatus,
      );
    },
    [irpVersion, portalType, callQueue, hasAniAliAccess, hasCallStatusAccess],
  );
  /**
   * Tracking function for primary locations
   * @param {Object} location - Primary location
   * @param {Boolean} timerBased - Flag to determine which tracking functions
   * to invoke
   * @param {Number} time - Number of seconds a hover occurred
   */
  const handlePrimaryLocationTracking = useCallback(
    (location, timerBased = false, time = 0) => {
      const isAniAli = isSourceAniAli(location);
      const serviceType = isAniAli ? ANI_ALI : getDataSource(location);
      const classOfServices = isAniAli
        ? location?.class_of_service || null
        : null;

      const callStatus = getMostRecentCallStatus({
        callerID: location?.caller_id,
        callQueue,
        hasAniAliAccess,
        hasCallStatusAccess,
      });

      const isConnectedCar = isCallerIDFromOrg(
        'sxm',
        callQueue,
        location?.caller_id,
      );

      const { payloadID, requestID } = isExtraInfoOrMeta(location);
      if (!timerBased) {
        trackHoverOnPin(irpVersion, serviceType, classOfServices, callStatus);
      } else {
        if (time >= 1 && time < 2) {
          trackHoverOnCallWithTime(
            1,
            'map',
            isConnectedCar,
            irpVersion,
            payloadID,
            requestID,
            portalType,
            serviceType,
            classOfServices,
            callStatus,
          );
        }
        if (time >= 2) {
          trackHoverOnCallWithTime(
            2,
            'map',
            isConnectedCar,
            irpVersion,
            payloadID,
            requestID,
            portalType,
            serviceType,
            classOfServices,
            callStatus,
          );
        }
      }
    },
    [irpVersion, portalType, callQueue, hasAniAliAccess, hasCallStatusAccess],
  );

  // Timer based tracking
  useEffect(() => {
    if (hoveredCallData) {
      if (timer < 2) {
        hoveredTimer.current = setTimeout(
          () => setTimer(timer => timer + 1),
          1000,
        );
      }

      if (timer >= 1 && timer < 2) {
        handlePrimaryLocationTracking(hoveredCallData, true, timer);
      }
      if (timer >= 2) {
        handlePrimaryLocationTracking(hoveredCallData, true, 2);
      }
    } else {
      setTimer(0);
      clearTimeout(hoveredTimer.current);
    }
    return () => {
      clearTimeout(hoveredTimer.current);
    };
  }, [handlePrimaryLocationTracking, hoveredCallData, timer]);

  // Normalize the data
  useEffect(() => {
    if (isAllView) {
      if (Object.keys(callQueue).length) {
        const locations = Object.values(callQueue)
          .map(call => {
            const {
              mostRecentLocation,
              mostRecentSecondaryLocation,
            } = getMostRecentLocations(call);
            // ONLY POPULATE PRIMARY LOCATION for mostRecentSecondaryLocation in ALL VIEW
            const location = !mostRecentLocation
              ? mostRecentSecondaryLocation
              : undefined;
            if (location) {
              return {
                ...location,
                isHistoric: false,
                callStatuses: call?.callStatuses,
              };
            }
          })
          .filter(loc => loc);
        const data = {
          primaryLocations: locations,
          secondaryLocations: [],
        };
        setNormalizedData(data);
      } else {
        // All calls have been removed
        setNormalizedData({
          primaryLocations: [],
          secondaryLocations: [],
        });
      }
    } else if (selectedCallerID) {
      if (
        callQueue[selectedCallerID]?.core?.locations?.length ||
        callQueue[selectedCallerID]?.core?.secondaryLocations?.length
      ) {
        const call = callQueue[selectedCallerID];
        const locations = call?.core?.locations?.map((location, idx) => {
          return {
            ...location,
            isHistoric: idx < call?.core?.locations?.length - 1,
            callStatuses: call?.callStatuses,
          };
        });
        const secondaryLocation =
          call?.core?.secondaryLocations[
            call?.core?.secondaryLocations?.length - 1
          ];
        // Primary Location is populated, but only visible on All View
        // It is being handled in Markers.js for event view
        const data = {
          primaryLocations: locations ? locations : [],
          secondaryLocations: secondaryLocation ? [secondaryLocation] : [], // Only one is drawn at any time
        };

        setNormalizedData(data);
      }
    }
  }, [
    callQueue,
    isAllView,
    selectedCallerID,
    dispatch,
    map,
    numberWithNewLocation,
  ]);

  // Draw the markers for all view or detailed view
  useEffect(() => {
    if (normalizedData) {
      const primaryCallsIconLayerProps = {
        iconAtlas: callsSprites,
        iconMapping: ICON_MAPPING,
        sizeUnits: 'meters',
        visible: isAllView,
        pickable: true,
        highlightColor: [255, 255, 255, 60],

        getIcon: d => {
          return getPrimaryCallIconName({
            location: d,
            isAllView,
            isHistoricLocation: d?.isHistoric,
            hasCallStatusAccess,
            callQueue,
            hasAniAliAccess,
          });
        },
        getPosition: d => {
          return [d?.longitude, d?.latitude];
        },
        opacity: activeQueueTab !== EMERGENCY_CALLS ? 0.2 : 1,
        ...MARKER_SIZES,
      };

      const mostRecentLocation =
        normalizedData?.primaryLocations[
          normalizedData?.primaryLocations?.length - 1
        ] || {}; // Most recent location is last

      const commonCircleLayerProps = {
        extruded: true,
        filled: true,
        getLineWidth: 2,
        lineWidthMaxPixels: 2,
        lineWidthMinPixels: 1,
        pickable: false, // If true, the layers respond to pointer picking events
        pointType: 'circle',
        stroked: true,
        radiusMinPixels: 1,

        getFillColor: d => {
          return [6, 192, 139, 51];
        },
        getLineColor: d => {
          return [6, 192, 139, 255];
        },
        getPosition: d => {
          return [d?.longitude, d?.latitude];
        },
        getRadius: d => {
          return d?.uncertainty_radius;
        },
      };
      const mostRecentCircleLayerProps = {
        data: [mostRecentLocation],
        visible: !!selectedCallerID,
        ...commonCircleLayerProps,
      };

      const historicCircleLayerProps = {
        data:
          hoveredCallData !== null &&
          !isEqual(hoveredCallData, mostRecentLocation)
            ? [hoveredCallData]
            : [],
        visible: true, // This will only render when there is data. Safe to set as true. Doesn't conflict with the most recent circle
        ...commonCircleLayerProps,
      };
      const secondaryCallsIconLayerProps = {
        iconAtlas: callsSprites,
        iconMapping: ICON_MAPPING,
        sizeUnits: 'meters',
        visible: !!selectedCallerID,
        pickable: true,
        highlightColor: [255, 255, 255, 60],

        getIcon: d => {
          return getSecondaryCallIconName({ isAllView, location: d });
        },
        getPosition: d => {
          return [d?.longitude, d?.latitude];
        },
        opacity: activeQueueTab !== EMERGENCY_CALLS ? 0.2 : 1,
        ...MARKER_SIZES,
      };

      // Won't get drawn when only 1 set of coordinates exist
      const pathData = [
        [
          [mostRecentLocation?.longitude, mostRecentLocation?.latitude],
          ...(normalizedData.secondaryLocations[0]
            ? [
                [
                  normalizedData.secondaryLocations?.[0]?.longitude,
                  normalizedData.secondaryLocations?.[0]?.latitude,
                ],
              ]
            : []),
        ],
      ];

      const secondaryCallPathLayerProps = {
        data: pathData,
        pickable: false,
        visible: !!selectedCallerID,
      };

      const callsLayer = new CallsLayer({
        data: normalizedData,
        primaryCallsIconLayerProps,
        mostRecentCircleLayerProps,
        historicCircleLayerProps,
        secondaryCallsIconLayerProps,
        secondaryCallPathLayerProps,
        autoHighlight: true,

        // Pointer picking events functions must be passed in this way instead of through the layerProps
        onClick: (d, event) => {
          if (d?.sourceLayer?.id.includes(CALL_ICONS_LAYER)) {
            const id = d.object?.caller_id;
            setHoveredCallData(null);
            dispatch(setSelectedCallerID(id));
            dispatch(setHoveredCallerID(null));
            dispatch(hideIncidentPopover());
            trackCallMarkerClicks(activeQueueTab, irpVersion);
            trackClickOnCallFromMap(irpVersion);
          }
        },

        onHover: (d, event) => {
          /**
           * @constant isSameLocObj - prevent re-computation when cursor moves inside the marker
           */

          const isSameLocObj =
            hoveredCallData &&
            d?.object &&
            hoveredCallData.caller_id === d?.object?.caller_id &&
            hoveredCallData.call_start_time === d?.object?.call_start_time;

          if (!isSameLocObj) {
            const layerID = d?.sourceLayer?.id;
            const isSecondaryLocation =
              (layerID.includes(CALL_ICONS_LAYER) ||
                layerID.includes(SECONDARY_LOCATION_ICON_LAYER)) &&
              d?.object;

            if (isSecondaryLocation) {
              handleHoverSecondaryLocation({ location: d.object });
              dispatch(setHoveredCallerID(d.object.caller_id));
            } else if (d?.object) {
              handleHoverPrimaryLocation({ location: d.object });
            } else {
              dispatch(setHoveredCallerID(null));
              setHoveredCallData(null);
              dispatch(hideIncidentPopover());
              overlay.setCursorDefault();
            }
          }
        },
      });
      if (overlay) {
        storeLayers.save(callsLayer);

        // Currently the "callsLayer" is only used for Google map. If the map provider is switched
        // to Esri map, delete the "callsLayer" from the layers collection.
        if (mapProvider === ESRI && storeLayers.getAll().includes(callsLayer)) {
          storeLayers.deleteLayer('CallsLayer');
        }

        overlay.setLayers(storeLayers.getAll());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeQueueTab,
    dispatch,
    handleHoverSecondaryLocation,
    hasCallStatusAccess,
    hoveredCallData,
    isAllView,
    normalizedData,
    overlay,
    selectedAlertID,
    selectedCallerID,
    callQueue,
    map,
  ]);

  useEffect(() => {
    if (
      selectedCallerID &&
      callQueue &&
      mapProvider === GOOGLE &&
      map &&
      ((numberWithNewLocation && numberWithNewLocation === selectedCallerID) ||
        !numberWithNewLocation)
    ) {
      const {
        mostRecentLocation,
        mostRecentSecondaryLocation,
      } = getMostRecentLocations(callQueue[selectedCallerID]);

      /**
       * @constant {shouldPan} - condition logic for map panning
       * when selecting a call with both DBH and SecondaryLocation:
       * The map will shifts between different locations.
       * This occurs because of a conflict of interest between the two layers (Google & deckGL).
       * Google pulls the selected caller ID into one location initially.
       * Then, deckGL promptly moves it to another location.
       */
      const shouldPan =
        (mostRecentLocation && prevSelectedCallerID === selectedCallerID) ||
        (!mostRecentLocation && mostRecentSecondaryLocation);

      if (shouldPan) {
        const mostRecentLoc = getMostRecentLoc(callQueue[selectedCallerID]);
        panGoogleMapTo({
          mapInstance: map.mapInstance,
          latitude: mostRecentLoc.latitude,
          longitude: mostRecentLoc.longitude,
        });
        const currentZoomLevel = map.mapInstance.getZoom();
        setGoogleMapZoomTo({
          mapInstance: map.mapInstance,
          zoom: !prevSelectedCallerID ? 16 : currentZoomLevel,
        });
      }

      dispatch(resetNumberWithNewLocation());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapProvider, selectedCallerID, numberWithNewLocation]);

  return <></>;
};

export default CallsGL;
